import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Users, Megaphone, Settings2, BarChart3, Trash2,
  Plus, Search, X, CheckCircle, User, Save, Shield, Bell
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPORTS = ["tennis","pickleball","golf","table-tennis","ski","padel","squash","badminton","climbing"];
const SPORT_LABELS = { tennis:"Tennis", pickleball:"Pickleball", golf:"Golf","table-tennis":"Table Tennis",ski:"Skiing",padel:"Padel",squash:"Squash",badminton:"Badminton",climbing:"Climbing" };

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, value, label, color = "text-brand-green" }) {
  return (
    <div className="bg-gray-100 border border-gray-200 rounded-xl p-5 text-center">
      <Icon size={20} className={`${color} mx-auto mb-2`} aria-hidden="true" />
      <div className="text-2xl font-heading font-black text-white">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function MembersTab({ club, myId, onUpdate, isOwner }) {
  const showToast = useToast();
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inviting, setInviting] = useState(null);

  const searchUsers = async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/partners/search?sport=&city=${encodeURIComponent(q)}&limit=8`, { withCredentials: true });
      setResults((data.partners || []).filter(p => p.id !== myId && !(club.members||[]).some(m=>m.user_id===p.id) && p.id !== club.owner_id));
    } catch { } finally { setSearching(false); }
  };

  const handleInvite = async (userId, userName) => {
    setInviting(userId);
    try {
      const { data } = await axios.post(`${API}/clubs/${club.id}/invite`, { user_id: userId }, { withCredentials: true });
      onUpdate(data);
      setResults(r => r.filter(p => p.id !== userId));
      showToast(`${userName} added to club!`);
    } catch (e) { showToast(e.response?.data?.detail || "Invite failed.", "error"); }
    finally { setInviting(null); }
  };

  const handleRemove = async (userId, userName) => {
    try {
      const { data } = await axios.delete(`${API}/clubs/${club.id}/members/${userId}`, { withCredentials: true });
      onUpdate(data);
      showToast(`${userName} removed.`);
    } catch (e) { showToast(e.response?.data?.detail || "Remove failed.", "error"); }
  };

  const handleWaiver = async (userId, userName) => {
    try {
      // Admin can waive on behalf (we only have self-sign, so skip for admin)
      showToast(`${userName} needs to sign the waiver themselves on the club page.`, "info");
    } catch { }
  };

  const allMembers = [
    { user_id: club.owner_id, user_name: club.owner_name, role: "admin", waiver_signed: true, user_avatar: "" },
    ...(club.members || [])
  ];
  const waiverRequired = club.settings?.require_waiver;

  return (
    <div className="space-y-6">
      {/* Invite */}
      <div className="bg-gray-100 border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-brand-navy mb-3">Invite Players</h3>
        <div className="relative mb-3">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search by city or name..."
            value={searchQ}
            onChange={e => { setSearchQ(e.target.value); searchUsers(e.target.value); }}
            className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 dark-section"
            data-testid="invite-search-input"
          />
        </div>
        {searching && <p className="text-xs text-gray-400 text-center py-2">Searching...</p>}
        {results.length > 0 && (
          <ul className="space-y-2 max-h-48 overflow-y-auto">
            {results.map(p => (
              <li key={p.id} className="flex items-center gap-3 py-1">
                <div className="w-7 h-7 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full rounded-full object-cover" /> : <User size={12} className="text-brand-green" />}
                </div>
                <span className="text-sm text-gray-600 flex-1">{p.name} · <span className="text-gray-400 text-xs">{p.city}</span></span>
                <button onClick={() => handleInvite(p.id, p.name)} disabled={inviting === p.id}
                  className="text-xs px-3 py-1 bg-brand-orange text-white font-bold rounded-lg hover:bg-brand-orange/90 transition-colors disabled:opacity-50" data-testid="invite-player-btn">
                  {inviting === p.id ? "..." : "Invite"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Member table */}
      <div className="bg-gray-100 border border-gray-200 rounded-xl overflow-hidden" data-testid="members-table">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm font-bold text-brand-navy">Current Members ({allMembers.length})</span>
          {waiverRequired && <span className="text-xs text-gray-500 flex items-center gap-1"><Shield size={11} /> Waiver required</span>}
        </div>
        <ul>
          {allMembers.map((m, idx) => (
            <li key={m.user_id} className={`flex items-center gap-3 px-5 py-3 ${idx < allMembers.length-1 ? "border-b border-gray-200" : ""}`}>
              <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                {m.user_avatar ? <img src={m.user_avatar} alt="" className="w-full h-full rounded-full object-cover" /> : <User size={13} className="text-brand-green" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">{m.user_name}</div>
                <div className="text-xs text-gray-400 capitalize">{m.role}</div>
              </div>
              {waiverRequired && (
                m.waiver_signed
                  ? <span className="flex items-center gap-1 text-xs text-brand-green"><CheckCircle size={12} /> Signed</span>
                  : <span className="text-xs text-yellow-400 border border-yellow-400/30 px-2 py-0.5 rounded-full">Pending</span>
              )}
              {isOwner && m.user_id !== club.owner_id && (
                <button onClick={() => handleRemove(m.user_id, m.user_name)} className="text-red-400 hover:text-red-300 transition-colors p-1" aria-label={`Remove ${m.user_name}`} data-testid={`remove-member-${m.user_id}`}>
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function AnnouncementsTab({ club, onUpdate }) {
  const showToast = useToast();
  const [form, setForm] = useState({ title: "", body: "" });
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handlePost = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/clubs/${club.id}/announcements`, form, { withCredentials: true });
      onUpdate(data);
      setForm({ title: "", body: "" });
      showToast("Announcement posted!");
    } catch { showToast("Failed to post announcement.", "error"); }
    finally { setPosting(false); }
  };

  const handleDelete = async (annId) => {
    setDeletingId(annId);
    try {
      const { data } = await axios.delete(`${API}/clubs/${club.id}/announcements/${annId}`, { withCredentials: true });
      onUpdate(data);
      showToast("Announcement deleted.");
    } catch { showToast("Failed to delete.", "error"); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="space-y-6">
      {/* Compose */}
      <form onSubmit={handlePost} className="bg-gray-100 border border-gray-200 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-bold text-brand-navy">New Announcement</h3>
        <input type="text" required value={form.title} onChange={e => setForm(p=>({...p,title:e.target.value}))}
          placeholder="Announcement title..." className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 dark-section" data-testid="announcement-title" />
        <textarea rows={3} required value={form.body} onChange={e => setForm(p=>({...p,body:e.target.value}))}
          placeholder="What do you want your members to know?" className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 resize-none dark-section" data-testid="announcement-body" />
        <button type="submit" disabled={posting} className="flex items-center gap-2 px-4 py-2 bg-brand-orange text-white text-sm font-bold rounded-lg hover:bg-lime/90 transition-colors disabled:opacity-50" data-testid="post-announcement-btn">
          <Bell size={13} aria-hidden="true" /> {posting ? "Posting..." : "Post Announcement"}
        </button>
      </form>

      {/* History */}
      {(club.announcements || []).length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-8">No announcements yet.</p>
      ) : (
        <div className="space-y-3" data-testid="announcements-list">
          {(club.announcements || []).map(a => (
            <div key={a.id} className="bg-gray-100 border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-bold text-brand-navy">{a.title}</h4>
                  <p className="text-sm text-gray-500 mt-1">{a.body}</p>
                  <p className="text-xs text-gray-400 mt-2">— {a.author_name} · {new Date(a.created_at).toLocaleDateString()}</p>
                </div>
                <button onClick={() => handleDelete(a.id)} disabled={deletingId === a.id} className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0 p-1" aria-label="Delete announcement" data-testid={`delete-announcement-${a.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab({ club, onUpdate }) {
  const showToast = useToast();
  const [form, setForm] = useState({
    name: club.name, sport: club.sport, city: club.city,
    description: club.description || "",
    require_waiver: club.settings?.require_waiver ?? false,
    waiver_text: club.settings?.waiver_text || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/clubs/${club.id}`, form, { withCredentials: true });
      onUpdate(data);
      showToast("Settings saved!");
    } catch { showToast("Failed to save.", "error"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSave} className="space-y-5" data-testid="club-settings-form">
      <div className="bg-gray-100 border border-gray-200 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-bold text-brand-navy">Club Info</h3>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="s-name">Club Name</label>
          <input id="s-name" type="text" required value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}
            className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green/50 dark-section" data-testid="settings-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="s-sport">Sport</label>
            <select id="s-sport" value={form.sport} onChange={e=>setForm(p=>({...p,sport:e.target.value}))}
              className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green/50 dark-section">
              {SPORTS.map(s=><option key={s} value={s}>{SPORT_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="s-city">City</label>
            <input id="s-city" type="text" required value={form.city} onChange={e=>setForm(p=>({...p,city:e.target.value}))}
              className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green/50 dark-section" />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="s-desc">Description</label>
          <textarea id="s-desc" rows={3} value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))}
            className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green/50 resize-none dark-section" data-testid="settings-description" />
        </div>
      </div>

      {/* Waiver */}
      <div className="bg-gray-100 border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-white" id="waiver-label">Require Waiver</h3>
            <p className="text-xs text-gray-500 mt-0.5">Members must sign before participating.</p>
          </div>
          <button type="button" role="switch" aria-checked={form.require_waiver} aria-labelledby="waiver-label"
            onClick={() => setForm(p=>({...p,require_waiver:!p.require_waiver}))}
            className={`w-10 h-6 rounded-full transition-colors relative ${form.require_waiver ? "bg-brand-green" : "bg-[#2A3143]"} dark-section`} data-testid="waiver-toggle">
            <span className="sr-only">{form.require_waiver ? "On" : "Off"}</span>
            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${form.require_waiver ? "right-1" : "left-1"}`} aria-hidden="true" />
          </button>
        </div>
        {form.require_waiver && (
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="s-waiver">Waiver Text</label>
            <textarea id="s-waiver" rows={4} value={form.waiver_text} onChange={e=>setForm(p=>({...p,waiver_text:e.target.value}))}
              placeholder="By signing this waiver, I acknowledge that..." className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 resize-none dark-section" data-testid="waiver-text" />
          </div>
        )}
      </div>

      <button type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 py-3 bg-brand-orange text-white font-bold text-sm rounded-lg hover:bg-lime/90 transition-colors disabled:opacity-50" data-testid="save-settings-btn">
        <Save size={15} aria-hidden="true" /> {saving ? "Saving..." : "Save Settings"}
      </button>
    </form>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────

export default function ClubDashboardPage() {
  const { clubId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clubs/${clubId}`, { withCredentials: true });
      setClub(data);
    } catch { navigate("/clubs"); }
    finally { setLoading(false); }
  }, [clubId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center"><div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" /></div>;
  if (!club) return null;

  const isOwner = club.owner_id === user?.id;
  const memberEntry = (club.members || []).find(m => m.user_id === user?.id);
  const isAdmin = isOwner || memberEntry?.role === "admin";

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-3">You need to be an admin to manage this club.</p>
          <Link to={`/clubs/${clubId}`} className="text-brand-green hover:underline text-sm">View Club →</Link>
        </div>
      </div>
    );
  }

  const allMembers = [{ user_id: club.owner_id }, ...(club.members || [])];

  const TABS = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "members",  label: "Members",  icon: Users },
    { id: "announce", label: "Announcements", icon: Megaphone },
    { id: "settings", label: "Settings", icon: Settings2 },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Link to={`/clubs/${clubId}`} className="text-gray-500 hover:text-brand-green transition-colors" aria-label="Back to club">
            <ArrowLeft size={18} aria-hidden="true" />
          </Link>
          <h1 className="text-2xl font-heading font-black text-white" data-testid="dashboard-title">{club.name}</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-lime/15 text-brand-green border border-brand-green/30">{isOwner ? "Owner" : "Admin"}</span>
        </div>
        <p className="text-gray-500 text-sm mb-8 ml-7">{SPORT_LABELS[club.sport]} · {club.city}</p>

        {/* Tab bar */}
        <div className="flex gap-1 bg-[#1A1F2E] border border-gray-200 rounded-xl p-1 mb-7 overflow-x-auto dark-section" role="tablist">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} role="tab" aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 flex-1 py-2 px-3 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${tab===t.id ? "bg-brand-orange text-white" : "text-gray-500 hover:text-white"}`}
                data-testid={`dashboard-tab-${t.id}`}>
                <Icon size={13} aria-hidden="true" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard icon={Users} value={allMembers.length} label="Total Members" />
              <StatCard icon={Megaphone} value={(club.announcements||[]).length} label="Announcements" color="text-sky-400" />
              <StatCard icon={Shield} value={(club.members||[]).filter(m=>m.waiver_signed).length + 1} label="Waivers Signed" color="text-yellow-400" />
              <StatCard icon={CheckCircle} value={(club.members||[]).filter(m=>m.role==="admin").length + 1} label="Admins" color="text-purple-400" />
            </div>
            <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-5 dark-section">
              <h3 className="text-sm font-bold text-brand-navy mb-3">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setTab("members")} className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 text-sm text-gray-600 rounded-lg hover:border-brand-green/40 transition-colors">
                  <Users size={13} /> Manage Members
                </button>
                <button onClick={() => setTab("announce")} className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 text-sm text-gray-600 rounded-lg hover:border-brand-green/40 transition-colors">
                  <Bell size={13} /> Post Announcement
                </button>
                <button onClick={() => setTab("settings")} className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 text-sm text-gray-600 rounded-lg hover:border-brand-green/40 transition-colors">
                  <Settings2 size={13} /> Club Settings
                </button>
                <Link to={`/clubs/${clubId}`} className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 text-sm text-gray-600 rounded-lg hover:border-brand-green/40 transition-colors">
                  View Public Page →
                </Link>
              </div>
            </div>
            {(club.announcements || []).length > 0 && (
              <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-5 dark-section">
                <h3 className="text-sm font-bold text-brand-navy mb-3">Latest Announcement</h3>
                <h4 className="text-sm font-medium text-white">{club.announcements[0].title}</h4>
                <p className="text-sm text-gray-500 mt-1">{club.announcements[0].body}</p>
              </div>
            )}
          </div>
        )}

        {tab === "members" && (
          <MembersTab club={club} myId={user?.id} isOwner={isOwner} onUpdate={setClub} />
        )}

        {tab === "announce" && (
          <AnnouncementsTab club={club} onUpdate={setClub} />
        )}

        {tab === "settings" && (
          <SettingsTab club={club} onUpdate={setClub} />
        )}
      </div>
    </div>
  );
}
